import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Globe, Mail, Phone, Pencil, Save, Building2, ExternalLink, RefreshCw, Crown } from "lucide-react";
import CompanyLogo from "@/components/CompanyLogo";
import TagCategorySelector, { TagCategoryDisplay } from "@/components/TagCategorySelector";
import { WT_TAG_FIELDS } from "@/lib/winetech_tags";
import ProductEditor, { ProductGallery, normalizeProducts } from "@/components/ProductEditor";
import ImageUpload from "@/components/ImageUpload";

/**
 * Participant details modal — public-facing rich view + edit form for the
 * registrant's per-event profile (description, website, contact, products).
 *
 * Props:
 *  - participant: object from /events/{id}/participants-public (must include id, company_id, ...)
 *  - eventId: string (parent event id)
 *  - canEdit: boolean — true if current user can edit (admin or self)
 *  - onClose: () => void
 *  - onSaved: () => void (refresh parent list)
 */
export default function ParticipantDetailsModal({ participant: p, eventId, canEdit, onClose, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [sources, setSources] = useState({});
  const [isMember, setIsMember] = useState(false);
  const [syncToCompany, setSyncToCompany] = useState(false);

  // Load editable form data when entering edit mode
  useEffect(() => {
    if (!editing) return;
    let alive = true;
    api.get(`/events/${eventId}/registrations/${p.id}/profile`)
      .then((r) => {
        if (!alive) return;
        // Extract the 7 tag arrays into a nested `tags` object for the form,
        // keeping the rest of the basic fields flat.
        const fields = r.data.fields || {};
        const tags = {};
        WT_TAG_FIELDS.forEach((f) => { tags[f] = fields[f] || []; });
        const flat = { ...fields };
        WT_TAG_FIELDS.forEach((f) => { delete flat[f]; });
        setForm({ ...flat, tags });
        setSources(r.data.sources || {});
        setIsMember(!!r.data.is_member);
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Impossible de charger le profil");
        setEditing(false);
      });
    return () => { alive = false; };
  }, [editing, eventId, p.id]);

  const save = async () => {
    if (!form) return;
    setLoading(true);
    try {
      // Flatten the tags object back into top-level tag_* fields for the API.
      const { tags, ...rest } = form;
      const payload = { ...rest, sync_to_company: syncToCompany, ...(tags || {}) };
      const res = await api.patch(`/events/${eventId}/registrations/${p.id}/profile`, payload);
      toast.success(res.data.synced_to_company
        ? "Profil mis à jour (événement + fiche WineTech)"
        : "Profil de l'événement mis à jour");
      onSaved?.();
      setEditing(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  // Display mode: show data straight from participant prop
  const displayValues = {
    description: p.description,
    website: p.website,
    contact_email: p.contact_email,
    contact_phone: p.contact_phone,
    products: normalizeProducts(p.products),
  };
  // Tags display values (one field per category)
  const tagDisplayValues = Object.fromEntries(
    WT_TAG_FIELDS.map((f) => [f, p[f] || []])
  );
  const hasAnyTags = WT_TAG_FIELDS.some((f) => (p[f] || []).length > 0);

  const hasAnyContent = displayValues.description || displayValues.website
    || displayValues.contact_email || displayValues.contact_phone
    || (displayValues.products && displayValues.products.length > 0)
    || hasAnyTags;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="participant-details-modal">
      <button onClick={onClose} className="absolute inset-0 bg-neutral-900/70 backdrop-blur-sm" aria-label="Fermer" />
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-start gap-4 border-b border-neutral-100 px-6 py-5">
          <CompanyLogo src={p.company_logo} name={p.company || p.first_name} size={56} className="rounded-xl shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-neutral-900">{p.first_name} {p.last_name}</h2>
              {p.is_member && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800" data-testid="participant-member-badge">
                  <Crown className="h-2.5 w-2.5" /> Membre WineTech
                </span>
              )}
              {p.ticket_name && (
                <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ backgroundColor: p.ticket_color || "#E33170" }}>{p.ticket_name}</span>
              )}
            </div>
            {p.job_title && <div className="mt-0.5 text-sm text-neutral-600">{p.job_title}</div>}
            {p.company && (
              <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-wine-700 font-semibold">
                <Building2 className="h-3.5 w-3.5" /> {p.company}
                {p.company_slug && (
                  <a href={`/members/${p.company_slug}`} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-wine-50 px-2 py-0.5 text-[10px] font-bold text-wine-700 hover:bg-wine-100">
                    Fiche <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canEdit && !editing && (
              <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-50 px-3 py-1.5 text-xs font-bold text-wine-700 hover:bg-wine-100" data-testid="participant-edit-btn">
                <Pencil className="h-3.5 w-3.5" /> Modifier
              </button>
            )}
            <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" aria-label="Fermer"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {!editing ? (
            <div className="space-y-5" data-testid="participant-details-view">
              {!hasAnyContent && (
                <div className="rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/50 p-8 text-center text-sm text-neutral-500" data-testid="participant-no-content">
                  Aucune information complémentaire renseignée pour cet événement.
                  {canEdit && <div className="mt-2 text-xs text-wine-700">Cliquez sur <strong>Modifier</strong> pour en ajouter.</div>}
                </div>
              )}
              {displayValues.description && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">À propos</div>
                  <p className="mt-1 text-sm text-neutral-800 whitespace-pre-line leading-relaxed" data-testid="participant-description">{displayValues.description}</p>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                {displayValues.website && (
                  <a href={displayValues.website.startsWith("http") ? displayValues.website : `https://${displayValues.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-white p-3 hover:bg-wine-50/40 transition" data-testid="participant-website">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-wine-50 text-wine-600"><Globe className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold uppercase text-neutral-500">Site web</div>
                      <div className="truncate text-sm font-semibold text-neutral-900">{displayValues.website.replace(/^https?:\/\//, "")}</div>
                    </div>
                    <ExternalLink className="h-3.5 w-3.5 text-neutral-400" />
                  </a>
                )}
                {displayValues.contact_email && (
                  <a href={`mailto:${displayValues.contact_email}`} className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-white p-3 hover:bg-wine-50/40 transition" data-testid="participant-email">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-wine-50 text-wine-600"><Mail className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold uppercase text-neutral-500">Email</div>
                      <div className="truncate text-sm font-semibold text-neutral-900">{displayValues.contact_email}</div>
                    </div>
                  </a>
                )}
                {displayValues.contact_phone && (
                  <a href={`tel:${displayValues.contact_phone}`} className="flex items-center gap-3 rounded-xl border border-neutral-100 bg-white p-3 hover:bg-wine-50/40 transition" data-testid="participant-phone">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-wine-50 text-wine-600"><Phone className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold uppercase text-neutral-500">Téléphone</div>
                      <div className="truncate text-sm font-semibold text-neutral-900">{displayValues.contact_phone}</div>
                    </div>
                  </a>
                )}
              </div>

              {displayValues.products && displayValues.products.length > 0 && (
                <div data-testid="participant-products">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Produits &amp; services</div>
                  <div className="mt-2">
                    <ProductGallery value={displayValues.products} testId="participant-product-gallery" />
                  </div>
                </div>
              )}

              {hasAnyTags && (
                <div data-testid="participant-wt-tags">
                  <TagCategoryDisplay values={tagDisplayValues} testId="participant-tags-display" />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4" data-testid="participant-details-edit">
              {!form ? (
                <div className="py-12 text-center text-sm text-neutral-500">Chargement…</div>
              ) : (
                <>
                  {isMember && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="participant-edit-hint-member">
                      Cette entreprise est <strong>membre WineTech</strong>. Les champs vides sont pré-remplis depuis sa fiche entreprise. Toute modification est sauvegardée pour cet événement uniquement, sauf si vous activez l&apos;option de mise à jour ci-dessous.
                    </div>
                  )}

                  {/* Visuals — pre-filled from the company profile */}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FieldEditor label="Logo" badge={sources.logo} testId="edit-logo">
                      <ImageUpload value={form.logo || ""} onChange={(url) => setForm({ ...form, logo: url })} label="Logo entreprise" testId="edit-logo-upload" />
                    </FieldEditor>
                    <FieldEditor label="Bannière" badge={sources.banner} testId="edit-banner">
                      <ImageUpload value={form.banner || ""} onChange={(url) => setForm({ ...form, banner: url })} label="Bannière 16:5" testId="edit-banner-upload" />
                    </FieldEditor>
                  </div>

                  <FieldEditor label="Description" badge={sources.description} testId="edit-description">
                    <textarea rows={4} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                              className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                              placeholder="Présentation pour cet événement…"
                              data-testid="edit-description-input" />
                  </FieldEditor>

                  <FieldEditor label="Site web" badge={sources.website} testId="edit-website">
                    <input type="url" value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })}
                           className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                           placeholder="https://…" data-testid="edit-website-input" />
                  </FieldEditor>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <FieldEditor label="Email de contact" badge={sources.contact_email} testId="edit-email">
                      <input type="email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                             className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                             placeholder="contact@…" data-testid="edit-email-input" />
                    </FieldEditor>
                    <FieldEditor label="Téléphone" badge={sources.contact_phone} testId="edit-phone">
                      <input value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                             className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                             placeholder="+33…" data-testid="edit-phone-input" />
                    </FieldEditor>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <FieldEditor label="LinkedIn" badge={sources.linkedin} testId="edit-linkedin">
                      <input type="url" value={form.linkedin || ""} onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                             className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                             placeholder="https://linkedin.com/company/…" data-testid="edit-linkedin-input" />
                    </FieldEditor>
                    <FieldEditor label="Twitter / X" badge={sources.twitter} testId="edit-twitter">
                      <input type="url" value={form.twitter || ""} onChange={(e) => setForm({ ...form, twitter: e.target.value })}
                             className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                             placeholder="https://x.com/…" data-testid="edit-twitter-input" />
                    </FieldEditor>
                  </div>

                  <FieldEditor label="Produits &amp; services" badge={sources.products} testId="edit-products">
                    <ProductEditor
                      value={form.products || []}
                      onChange={(next) => setForm({ ...form, products: next })}
                      testId="edit-products-list"
                    />
                  </FieldEditor>

                  <details className="rounded-xl border border-neutral-200" data-testid="edit-tags-section">
                    <summary className="cursor-pointer px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-neutral-600 hover:bg-neutral-50 rounded-xl">
                      Tags d&apos;exploration ({Object.values(form.tags || {}).reduce((a, b) => a + b.length, 0)})
                    </summary>
                    <div className="border-t border-neutral-100 p-3">
                      <TagCategorySelector
                        values={form.tags || {}}
                        onChange={(k, next) => setForm({ ...form, tags: { ...(form.tags || {}), [k]: next } })}
                        compact
                        testId="edit-tags"
                      />
                    </div>
                  </details>

                  {isMember && (
                    <label className="flex items-start gap-3 rounded-xl border border-wine-200 bg-wine-50/40 p-3 cursor-pointer" data-testid="sync-to-company-label">
                      <input type="checkbox" checked={syncToCompany} onChange={(e) => setSyncToCompany(e.target.checked)} className="mt-0.5 accent-wine-500" data-testid="sync-to-company-checkbox" />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-wine-800 inline-flex items-center gap-1.5">
                          <RefreshCw className="h-3.5 w-3.5" /> Mettre à jour aussi mon profil entreprise WineTech
                        </div>
                        <div className="mt-0.5 text-xs text-wine-700">Les modifications ci-dessus seront répercutées sur la fiche publique de votre entreprise (visible sur l&apos;annuaire WineTech).</div>
                      </div>
                    </label>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {editing && (
          <div className="border-t border-neutral-100 px-6 py-4 flex justify-end gap-2">
            <button onClick={() => setEditing(false)} disabled={loading} className="rounded-full bg-neutral-100 px-5 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-200 disabled:opacity-50" data-testid="edit-cancel-btn">Annuler</button>
            <button onClick={save} disabled={loading || !form} className="inline-flex items-center gap-2 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid="edit-save-btn">
              <Save className="h-3.5 w-3.5" /> {loading ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldEditor({ label, badge, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2">
        <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</label>
        {badge === "company" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold text-amber-700">Issu de la fiche WineTech</span>}
        {badge === "event" && <span className="rounded-full bg-wine-100 px-2 py-0.5 text-[9px] font-bold text-wine-700">Spécifique à cet événement</span>}
      </div>
      {children}
    </div>
  );
}
